// src/pages/settings/sms/components/SmsLogsPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { History, RefreshCw } from 'lucide-react';
import { getSmsLogs } from '../../../../services/api/smsConfig';
import { useAlert } from '../../../../contexts/AlertContext';
import DataTable from '../../../../components/common/DataTable/DataTable';
import styles from './SmsLogsPanel.module.css';

const STATUS_STYLES = {
  sent:   { bg: 'var(--color-success-bg, #ecfdf5)', color: 'var(--color-success-dark, #065f46)', border: 'var(--color-success-border, #a7f3d0)' },
  failed: { bg: 'var(--color-error-bg)', color: 'var(--color-error-dark)', border: 'var(--color-error-border)' },
  queued: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.queued;
  return (
    <span style={{
      display: 'inline-block',
      padding: '0.2rem 0.6rem',
      borderRadius: '4px',
      fontSize: '0.7rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
    }}>
      {status}
    </span>
  );
};

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const COLUMNS = [
  {
    key: 'createdAt',
    header: 'Date / Time',
    width: '160px',
    render: (row) => {
      const d = new Date(row.createdAt);
      return (
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
          {d.toLocaleDateString()} {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      );
    },
  },
  {
    key: 'orderNumber',
    header: 'Order #',
    width: '140px',
    render: (row) => row.orderNumber
      ? <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{row.orderNumber}</span>
      : <span style={{ color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>—</span>,
  },
  {
    key: 'phoneNumber',
    header: 'Phone Number',
    width: '150px',
    render: (row) => (
      <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{row.phoneNumber}</span>
    ),
  },
  {
    key: 'message',
    header: 'Message',
    render: (row) => (
      <span className={styles.messageCell} title={row.message}>{row.message}</span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '90px',
    align: 'center',
    render: (row) => <StatusBadge status={row.status} />,
  },
  {
    key: 'errorMessage',
    header: 'Error',
    width: '200px',
    render: (row) => row.errorMessage
      ? <span style={{ fontSize: '0.8rem', color: 'var(--color-error-dark)' }} title={row.errorMessage}>{row.errorMessage}</span>
      : <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>,
  },
];

const SmsLogsPanel = () => {
  const { error: showError } = useAlert();
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 20, total: 0, pageSizeOptions: PAGE_SIZE_OPTIONS });

  const load = useCallback(async (page = 1, pageSize = 20) => {
    try {
      setIsLoading(true);
      const res = await getSmsLogs({ page, pageSize });
      setLogs(res.logs || []);
      setPagination(prev => ({ ...prev, currentPage: page, pageSize, total: res.total || 0 }));
    } catch (err) {
      showError('Failed to load SMS logs');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(1, pagination.pageSize); }, [load]);

  const handlePaginationChange = ({ currentPage, pageSize }) => {
    load(currentPage, pageSize);
  };

  const handleRefresh = () => load(pagination.currentPage, pagination.pageSize);

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div>
          <h3 className={styles.panelTitle}>SMS Log History</h3>
          <p className={styles.panelDesc}>All SMS messages dispatched by this business, newest first.</p>
        </div>
        <button className={styles.refreshBtn} onClick={handleRefresh} disabled={isLoading} title="Refresh">
          <RefreshCw size={15} className={isLoading ? styles.spin : ''} />
          Refresh
        </button>
      </div>

      <DataTable
        data={logs}
        columns={COLUMNS}
        loading={isLoading}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        emptyMessage="No SMS logs yet"
        emptyDescription="SMS messages will appear here once your triggers start firing."
        emptyIcon={<History size={40} />}
      />
    </div>
  );
};

export default SmsLogsPanel;